"use strict";
import { useAccount } from "hooks/useAccount";
import useCurrentBlockTimestamp from "hooks/useCurrentBlockTimestamp";
import useBlockNumber from "lib/hooks/useBlockNumber";
import ms from "ms";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { CanceledError, retry } from "state/activity/polling/retry";
import { useAppDispatch } from "state/hooks";
import { useMultichainTransactions, useTransactionRemover } from "state/transactions/hooks";
import { checkedTransaction } from "state/transactions/reducer";
import { isPendingTx } from "state/transactions/utils";
import { TransactionStatus } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { getChainInfo } from "features/chains/chainInfo";
import { TransactionReceiptNotFoundError } from "viem";
import { usePublicClient } from "wagmi";
export function shouldCheck(lastBlockNumber, tx) {
  if (tx.receipt) {
    return false;
  }
  if (!tx.lastCheckedBlockNumber) {
    return true;
  }
  const blocksSinceCheck = lastBlockNumber - tx.lastCheckedBlockNumber;
  if (blocksSinceCheck < 1) {
    return false;
  }
  const minutesPending = ((/* @__PURE__ */ new Date()).getTime() - tx.addedTime) / ms(`1m`);
  if (minutesPending > 60) {
    return blocksSinceCheck > 9;
  } else if (minutesPending > 5) {
    return blocksSinceCheck > 2;
  } else {
    return true;
  }
}
const DEFAULT_RETRY_OPTIONS = { n: 1, minWait: 0, maxWait: 0 };
const REPLACEMENT_RECEIPT_TIMEOUT_MS = 2e4;
function usePendingTransactions(chainId) {
  const multichainTransactions = useMultichainTransactions();
  return useMemo(() => {
    if (!chainId) {
      return [];
    }
    return multichainTransactions.flatMap(([tx, txChainId]) => {
      if (isPendingTx(
        tx,
        /* skipDepositedBridgeTxs = */
        true
      ) && txChainId === chainId) {
        return tx.batchInfo ? [] : [tx];
      } else {
        return [];
      }
    });
  }, [chainId, multichainTransactions]);
}
export function usePollPendingTransactions(onActivityUpdate) {
  const account = useAccount();
  const publicClient = usePublicClient();
  const pendingTransactions = usePendingTransactions(account.chainId);
  const hasPending = pendingTransactions.length > 0;
  const blockTimestamp = useCurrentBlockTimestamp({ refetchInterval: !hasPending ? false : void 0 });
  const lastBlockNumber = useBlockNumber();
  const removeTransaction = useTransactionRemover();
  const dispatch = useAppDispatch();
  const replacementLookups = useRef(/* @__PURE__ */ new Set());
  const removeIfStale = useCallback(
    (tx) => {
      if (!account.isConnected) {
        return;
      }
      if (tx.deadline) {
        if (blockTimestamp && tx.deadline < Number(blockTimestamp)) {
          removeTransaction(tx.hash);
        }
      } else if (tx.addedTime + ms(`6h`) < Date.now()) {
        removeTransaction(tx.hash);
      }
    },
    [account.isConnected, blockTimestamp, removeTransaction]
  );
  const getReceipt = useCallback(
    (tx) => {
      if (!publicClient || !account.chainId) {
        throw new Error("No publicClient or chainId");
      }
      const retryOptions = getChainInfo(account.chainId)?.pendingTransactionsRetryOptions ?? DEFAULT_RETRY_OPTIONS;
      return retry(
        () => (
          // viem's `getTransactionReceipt` throws `TransactionReceiptNotFoundError` while a tx is
          // unmined (it never resolves to null). Run stale-GC on that path, then rethrow as-is: the
          // error is not a `RetryableError`, so `retry` rejects after a single call, preserving the
          // original one-request-per-check cadence (the effect re-checks on subsequent blocks).
          publicClient.getTransactionReceipt({ hash: tx.hash }).catch((error) => {
            if (error instanceof TransactionReceiptNotFoundError) {
              removeIfStale(tx);
            }
            throw error;
          })
        ),
        retryOptions
      );
    },
    [account.chainId, publicClient, removeIfStale]
  );
  useEffect(() => {
    const chainId = account.chainId;
    if (!chainId || !publicClient || !lastBlockNumber || !hasPending) {
      return void 0;
    }
    let stale = false;
    const handleReplacement = async (tx) => {
      if (typeof tx.nonce !== "number" || !tx.from) {
        return;
      }
      const lookupKey = `${chainId}:${tx.hash}`;
      if (replacementLookups.current.has(lookupKey)) {
        return;
      }
      let currentNonce;
      try {
        currentNonce = await publicClient.getTransactionCount({
          address: tx.from,
          blockTag: "latest"
        });
      } catch {
        return;
      }
      if (stale || currentNonce <= tx.nonce) {
        return;
      }
      replacementLookups.current.add(lookupKey);
      let reason;
      try {
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: tx.hash,
          timeout: REPLACEMENT_RECEIPT_TIMEOUT_MS,
          onReplaced: (replacement) => {
            reason = replacement.reason;
          }
        });
        if (stale) {
          return;
        }
        if (reason === "cancelled") {
          removeTransaction(tx.hash);
          return;
        }
        onActivityUpdate({
          type: "transaction",
          chainId,
          original: tx,
          update: {
            status: receipt.status === "success" ? TransactionStatus.Confirmed : TransactionStatus.Failed,
            info: tx.info
          }
        });
      } catch {
        if (!stale) {
          removeTransaction(tx.hash);
        }
      } finally {
        replacementLookups.current.delete(lookupKey);
      }
    };
    const cancels = pendingTransactions.filter((tx) => shouldCheck(lastBlockNumber, tx)).map((tx) => {
      const { promise, cancel } = getReceipt(tx);
      promise.then((receipt) => {
        if (stale) {
          return;
        }
        onActivityUpdate({
          type: "transaction",
          chainId,
          original: tx,
          update: {
            status: receipt.status === "success" ? TransactionStatus.Confirmed : TransactionStatus.Failed,
            info: tx.info
          }
        });
      }).catch((error) => {
        if (error instanceof CanceledError || stale) {
          return;
        }
        if (error instanceof TransactionReceiptNotFoundError) {
          void handleReplacement(tx);
        }
        dispatch(checkedTransaction({ chainId, hash: tx.hash, blockNumber: lastBlockNumber }));
      });
      return cancel;
    });
    return () => {
      stale = true;
      cancels.forEach((cancel) => cancel());
    };
  }, [
    account.chainId,
    publicClient,
    lastBlockNumber,
    getReceipt,
    pendingTransactions,
    hasPending,
    dispatch,
    onActivityUpdate,
    removeTransaction
  ]);
}
